import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy, inject } from '@angular/core';
import { CanMatchFn, NavigationError, Router } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';

export interface DeploymentManifest {
  buildId: string;
  version?: string;
  generatedAt?: string;
}

const VERSION_MANIFEST_PATH = 'version.json';
const INDEX_PATH = 'index.html';
const UPDATE_RELOAD_KEY = 'ZIGNFLIX_DEPLOYMENT_RELOAD';

function extractMainScript(source: string): string | null {
  return source.match(/(?:src=["'])([^"']*main(?:-[^"'/]+)?\.js(?:\?[^"']*)?)/i)?.[1]?.split('/').pop()?.split('?')[0] ?? null;
}

function currentBuildId(): string {
  const mainScript = Array.from(document.scripts)
    .map(script => script.src)
    .find(source => /(?:^|\/)main(?:-[^/]+)?\.js(?:\?|$)/i.test(source));
  if (!mainScript) return 'development';
  return new URL(mainScript, document.baseURI).pathname.split('/').pop() || 'development';
}

async function fetchNoCache(path: string): Promise<Response | null> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 5000);

  try {
    const url = new URL(path, document.baseURI);
    url.searchParams.set('_', Date.now().toString());
    return await fetch(url.toString(), {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache'
      },
      signal: controller.signal
    });
  } catch {
    return null;
  } finally {
    window.clearTimeout(timeout);
  }
}

export async function fetchDeploymentManifest(): Promise<DeploymentManifest | null> {
  try {
    const response = await fetchNoCache(VERSION_MANIFEST_PATH);
    if (!response?.ok) return null;
    const manifest = await response.json() as Partial<DeploymentManifest>;
    return typeof manifest.buildId === 'string' && manifest.buildId
      ? manifest as DeploymentManifest
      : null;
  } catch {
    // A version check must never prevent the application from starting offline.
    return null;
  }
}

async function fetchDeploymentIndexBuild(): Promise<string | null> {
  const response = await fetchNoCache(INDEX_PATH);
  if (!response?.ok) return null;
  return extractMainScript(await response.text());
}

async function fetchServerDeployment(): Promise<DeploymentManifest | null> {
  const manifest = await fetchDeploymentManifest();
  if (manifest?.buildId && manifest.buildId !== 'development') return manifest;

  const indexBuildId = await fetchDeploymentIndexBuild();
  if (!indexBuildId) return manifest;

  return {
    buildId: indexBuildId,
    version: manifest?.version && manifest.version !== 'development' ? manifest.version : undefined,
    generatedAt: manifest?.generatedAt
  };
}

export async function ensureCurrentDeployment(): Promise<boolean> {
  const manifest = await fetchServerDeployment();
  const loadedBuildId = currentBuildId();
  if (!manifest || manifest.buildId === 'development' || manifest.buildId === loadedBuildId || loadedBuildId === 'development') return true;
  return !reloadForDeployment(manifest.buildId);
}

export function reloadForDeployment(buildId: string): boolean {
  const reloadMarker = `${currentBuildId()}->${buildId}`;
  if (sessionStorage.getItem(UPDATE_RELOAD_KEY) === reloadMarker) return false;
  sessionStorage.setItem(UPDATE_RELOAD_KEY, reloadMarker);

  if ('caches' in window) {
    void caches.keys().then(keys => Promise.all(keys.map(key => caches.delete(key))));
  }

  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.set('_build', buildId);
  window.location.replace(nextUrl.toString());
  return true;
}

@Injectable({ providedIn: 'root' })
export class DeploymentVersionService implements OnDestroy {
  private readonly subscriptions = new Subscription();
  private readonly updateInProgressSubject = new BehaviorSubject<boolean>(false);
  readonly updateInProgress$ = this.updateInProgressSubject.asObservable();
  private intervalId?: number;
  private checking = false;
  private started = false;

  constructor(
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  start(): void {
    if (this.started || currentBuildId() === 'development') return;
    this.started = true;

    this.subscriptions.add(this.router.events.subscribe(event => {
      if (event instanceof NavigationError && this.isChunkLoadFailure(event.error)) {
        this.updateInProgressSubject.next(true);
        void this.checkForUpdate();
      }
    }));

    this.document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('focus', this.onWindowFocus);
    window.addEventListener('online', this.onWindowFocus);
    window.addEventListener('unhandledrejection', this.onUnhandledRejection);
    this.intervalId = window.setInterval(() => void this.checkForUpdate(), 5 * 60 * 1000);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.intervalId) window.clearInterval(this.intervalId);
    this.document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('focus', this.onWindowFocus);
    window.removeEventListener('online', this.onWindowFocus);
    window.removeEventListener('unhandledrejection', this.onUnhandledRejection);
  }

  async checkForUpdate(): Promise<void> {
    if (this.checking) return;
    this.checking = true;
    try {
      await this.ensureCurrentDeployment();
    } finally {
      this.checking = false;
    }
  }

  async ensureCurrentDeployment(): Promise<boolean> {
    const manifest = await fetchServerDeployment();
    const loadedBuildId = currentBuildId();

    if (!manifest || manifest.buildId === 'development' || manifest.buildId === loadedBuildId || loadedBuildId === 'development') {
      this.updateInProgressSubject.next(false);
      return true;
    }

    this.updateInProgressSubject.next(true);
    const reloading = reloadForDeployment(manifest.buildId);
    if (!reloading) {
      this.updateInProgressSubject.next(false);
    }
    return !reloading;
  }

  private readonly onVisibilityChange = (): void => {
    if (this.document.visibilityState === 'visible') void this.checkForUpdate();
  };

  private readonly onWindowFocus = (): void => {
    void this.checkForUpdate();
  };

  private readonly onUnhandledRejection = (event: PromiseRejectionEvent): void => {
    if (this.isChunkLoadFailure(event.reason)) {
      this.updateInProgressSubject.next(true);
      void this.checkForUpdate();
    }
  };

  private isChunkLoadFailure(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error ?? '');
    return /chunkloaderror|loading chunk|failed to fetch dynamically imported module|importing a module script failed/i.test(message);
  }
}

export const canMatchCurrentDeployment: CanMatchFn = () => {
  return inject(DeploymentVersionService).ensureCurrentDeployment();
};
